import { useEffect, useState } from "react";
import {
  creerRequeteProduction,
  deciderProduction,
  getProduction,
  objecterProduction,
} from "@/lib/apiProcedure";
import type { RedfernLine, StatutProduction } from "@/types/procedure";

interface Props {
  caseId: string | number;
  /** Camp de la partie connectée (avocat) : active la création / l'objection. */
  userSide?: "DEMANDEUR" | "DEFENDEUR" | null;
  /** Vue tribunal : active les décisions par ligne. */
  isArbiter?: boolean;
}

function apiDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? fallback
  );
}

const STATUT_STYLE: Record<StatutProduction, string> = {
  REQUETE: "bg-amber-100 text-amber-800",
  ACCORDEE: "bg-green-100 text-green-800",
  REJETEE: "bg-red-100 text-red-800",
  PARTIELLE: "bg-blue-100 text-blue-800",
};

/**
 * Redfern Schedule (IBA art. 3) — production de documents.
 * Partie : crée une demande à l'adverse / objecte aux demandes la visant.
 * Tribunal : tranche chaque ligne (accordée / rejetée / partielle).
 */
export function RedfernSchedule({ caseId, userSide, isArbiter }: Props) {
  const [lines, setLines] = useState<RedfernLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [docs, setDocs] = useState("");
  const [pert, setPert] = useState("");
  const [objLine, setObjLine] = useState<string | null>(null);
  const [objText, setObjText] = useState("");
  const [decLine, setDecLine] = useState<string | null>(null);
  const [decChoice, setDecChoice] = useState("ACCORDEE");
  const [decMotif, setDecMotif] = useState("");

  const adverse = userSide === "DEMANDEUR" ? "DEFENDEUR" : "DEMANDEUR";

  const load = () => {
    getProduction(caseId)
      .then((res) => setLines(res.data))
      .catch(() => setLines([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const wrap = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      load();
      return true;
    } catch (e) {
      setErr(apiDetail(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await wrap(
      () =>
        creerRequeteProduction(caseId, {
          partie_requise: adverse,
          documents_demandes: docs,
          pertinence: pert,
        }),
      "Échec de la demande de production.",
    );
    if (ok) {
      setDocs("");
      setPert("");
    }
  };

  const handleObjecter = async (id: string) => {
    const ok = await wrap(
      () => objecterProduction(caseId, id, objText),
      "Échec de l'objection.",
    );
    if (ok) {
      setObjLine(null);
      setObjText("");
    }
  };

  const handleDecider = async (id: string) => {
    const ok = await wrap(
      () =>
        deciderProduction(caseId, id, {
          decision: decChoice,
          motif: decMotif.trim() || undefined,
        }),
      "Échec de la décision.",
    );
    if (ok) {
      setDecLine(null);
      setDecMotif("");
      setDecChoice("ACCORDEE");
    }
  };

  if (loading) return null;

  return (
    <div className="border rounded-lg p-4 mt-6 bg-white">
      <h3 className="text-lg font-bold text-gray-800 mb-1">
        Production de documents — Redfern Schedule
      </h3>
      <p className="text-xs text-gray-500 mb-4">IBA Rules on the Taking of Evidence, art. 3.</p>

      {err && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          {err}
        </div>
      )}

      {/* Création (partie) */}
      {userSide && (
        <form
          onSubmit={handleCreate}
          className="mb-5 p-3 bg-gray-50 rounded space-y-2"
        >
          <div className="text-xs font-medium text-gray-600">
            Demander des documents à la partie {adverse.toLowerCase()}
          </div>
          <input
            value={docs}
            onChange={(e) => setDocs(e.target.value)}
            required
            placeholder="Document(s) / catégorie demandés"
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
          />
          <textarea
            value={pert}
            onChange={(e) => setPert(e.target.value)}
            required
            placeholder="Pertinence & matérialité (pourquoi ces documents sont nécessaires)…"
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
            rows={2}
          />
          <button
            type="submit"
            disabled={busy}
            className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
          >
            Ajouter la demande
          </button>
        </form>
      )}

      {/* Tableau Redfern */}
      {lines.length === 0 ? (
        <p className="text-sm text-gray-400 italic">
          Aucune demande de production pour le moment.
        </p>
      ) : (
        <div className="space-y-3">
          {lines.map((l) => {
            const peutObjecter =
              !!userSide &&
              l.partie_requise === userSide &&
              l.statut === "REQUETE" &&
              !l.objection;
            const peutDecider = !!isArbiter && l.statut === "REQUETE";
            return (
              <div key={l.id} className="border rounded p-3 text-sm">
                <div className="flex justify-between items-start gap-3">
                  <div className="font-medium text-gray-800">
                    {l.documents_demandes}
                  </div>
                  <span
                    className={`shrink-0 text-xs px-2 py-1 rounded-full font-bold ${STATUT_STYLE[l.statut]}`}
                  >
                    {l.statut}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Demandé à : {l.partie_requise} · Pertinence : {l.pertinence}
                </p>
                {l.objection && (
                  <p className="text-xs text-amber-700 mt-1">
                    Objection : {l.objection}
                  </p>
                )}
                {l.decision_motif && (
                  <p className="text-xs text-gray-600 mt-1">
                    Décision : {l.decision_motif}
                  </p>
                )}

                {/* Action : objecter (partie requise) */}
                {peutObjecter && objLine !== l.id && (
                  <button
                    onClick={() => {
                      setObjLine(l.id);
                      setObjText("");
                    }}
                    className="mt-2 text-xs text-blue-600 underline"
                  >
                    Objecter
                  </button>
                )}
                {peutObjecter && objLine === l.id && (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={objText}
                      onChange={(e) => setObjText(e.target.value)}
                      placeholder="Motif d'objection (privilège, proportionnalité…)"
                      className="w-full rounded border p-2 text-sm bg-white text-gray-900"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={busy || objText.trim().length < 5}
                        onClick={() => handleObjecter(l.id)}
                        className="bg-amber-600 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                      >
                        Soumettre l'objection
                      </button>
                      <button
                        onClick={() => setObjLine(null)}
                        className="text-gray-500 text-xs"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Action : décider (tribunal) */}
                {peutDecider && decLine !== l.id && (
                  <button
                    onClick={() => {
                      setDecLine(l.id);
                      setDecChoice("ACCORDEE");
                      setDecMotif("");
                    }}
                    className="mt-2 text-xs text-green-700 underline"
                  >
                    Trancher
                  </button>
                )}
                {peutDecider && decLine === l.id && (
                  <div className="mt-2 space-y-2">
                    <select
                      value={decChoice}
                      onChange={(e) => setDecChoice(e.target.value)}
                      className="rounded border p-1.5 text-sm bg-white text-gray-900"
                    >
                      <option value="ACCORDEE">Accordée</option>
                      <option value="PARTIELLE">Partielle</option>
                      <option value="REJETEE">Rejetée</option>
                    </select>
                    <textarea
                      value={decMotif}
                      onChange={(e) => setDecMotif(e.target.value)}
                      placeholder="Motif (obligatoire si rejet/partielle)"
                      className="w-full rounded border p-2 text-sm bg-white text-gray-900"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={busy}
                        onClick={() => handleDecider(l.id)}
                        className="bg-green-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                      >
                        Rendre la décision
                      </button>
                      <button
                        onClick={() => setDecLine(null)}
                        className="text-gray-500 text-xs"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
